/**
 * RemindMeButton - Button to set game reminders
 * Uses push notifications if available, falls back to in-app reminders
 */
import { useState, useEffect } from 'react';
import { Bell, BellOff, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const RemindMeButton = ({ 
  broadcastId, 
  gameTitle,
  scheduledDate,
  scheduledTime,
  className = '',
  size = 'default' // 'default' | 'small' | 'icon'
}) => {
  const [isReminded, setIsReminded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    setIsLoggedIn(!!token);
    
    if (token) {
      checkReminderStatus();
    }
  }, [broadcastId]);

  const checkReminderStatus = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const res = await axios.get(`${API}/users/preferences/game-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const alerts = res.data.alerts || [];
      const hasAlert = alerts.some(a => a.broadcast_id === broadcastId);
      setIsReminded(hasAlert);
    } catch (err) {
      console.log('Could not check reminder status');
    }
  };

  const handleToggleReminder = async () => {
    if (!isLoggedIn) {
      // Redirect to login or show login prompt
      window.location.href = '/login-user?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('userToken');

    try {
      if (isReminded) {
        // Remove reminder
        await axios.delete(`${API}/users/preferences/game-alerts/${broadcastId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsReminded(false);
      } else {
        // Add reminder
        await axios.post(`${API}/users/preferences/game-alerts`, {
          broadcast_id: broadcastId,
          notify_minutes_before: 15
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsReminded(true);

        // Try to request push notification permission
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      }
    } catch (err) {
      console.error('Failed to toggle reminder:', err);
    } finally {
      setLoading(false);
    }
  };

  // Size variants
  const sizeClasses = {
    default: 'px-3 py-2 text-xs',
    small: 'px-2 py-1 text-[10px]',
    icon: 'p-2'
  };

  const iconSize = {
    default: 'w-4 h-4',
    small: 'w-3 h-3',
    icon: 'w-4 h-4'
  };

  if (size === 'icon') {
    return (
      <button
        onClick={handleToggleReminder}
        disabled={loading}
        className={`${className} ${isReminded ? 'text-orange-500' : 'text-zinc-500 hover:text-white'} transition-colors disabled:opacity-50`}
        title={isReminded ? 'Remove reminder' : 'Remind me'}
        data-testid={`remind-me-${broadcastId}`}
      >
        {loading ? (
          <Loader2 className={`${iconSize[size]} animate-spin`} />
        ) : isReminded ? (
          <Bell className={`${iconSize[size]} fill-current`} />
        ) : (
          <Bell className={iconSize[size]} />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleToggleReminder}
      disabled={loading}
      className={`
        ${className}
        ${sizeClasses[size]}
        flex items-center gap-1.5 font-secondary font-bold uppercase tracking-wide
        ${isReminded 
          ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30' 
          : 'bg-zinc-800 text-zinc-400 hover:text-white border border-white/10 hover:border-white/20'
        }
        transition-all disabled:opacity-50
      `}
      data-testid={`remind-me-${broadcastId}`}
    >
      {loading ? (
        <Loader2 className={`${iconSize[size]} animate-spin`} />
      ) : isReminded ? (
        <>
          <Check className={iconSize[size]} />
          {size !== 'small' && 'Reminded'}
        </>
      ) : (
        <>
          <Bell className={iconSize[size]} />
          {size !== 'small' && 'Remind Me'}
        </>
      )}
    </button>
  );
};

export default RemindMeButton;
